import { useGetFaireAcademyPageState } from "@faire/retailer-visitor-shared/hooks/openWithFaire/useGetFaireAcademyPageState";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { ChapterCompleteFeedback } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/components/ChapterCompleteFeedback";
import { ChapterPreview } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/components/ChapterPreview/index";
import { useGuideStore } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/useGuideStore";
import { FaireAcademyChapter } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/types";
import { Storage } from "@faire/web--source/common/Storage";
import { Spacer } from "@faire/web--source/slate/spacing";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { WhenVisibilityIsImminent } from "@faire/web--source/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { useMutation as useReadChapterMutation } from "@faire/web-api--source/endpoints/www/api/faire-academy/chapterIdentifier/read/post-hooks";
import { trackOpenWithFaireFaireAcademyReadReceiptsViewView } from "@faire/web-api--source/events/openWithFaire/view/faireAcademyReadReceiptsView";
import { IFaireAcademyChapter } from "@faire/web-api--source/indigofair/data/IFaireAcademyChapter";
import React, { Suspense } from "react";
import { useState, useEffect } from "react";

const FA_CHAPTER_VIEWS_KEY = "FA_CHAPTER_VIEWS";
const CHAPTER_VIEWS_LIMIT = 5;

export const ChapterBody: React.FC<{
  chapter: FaireAcademyChapter;
  nextChapter: FaireAcademyChapter | undefined;
  readChapters: Set<
    keyof typeof IFaireAcademyChapter.ChapterIdentifier | undefined
  >;
  handleReadChapter: (
    chapterId: IFaireAcademyChapter.ChapterIdentifier
  ) => void;
}> = ({ chapter, nextChapter, readChapters, handleReadChapter }) => {
  const [{ guide }] = useGuideStore(["guide"]);
  const { pageState } = useGetFaireAcademyPageState();
  const ActiveChapter = chapter?.ChapterComponent;
  const chapterId = chapter.id;
  const isChapterRead = readChapters.has(chapterId);

  const { mutate } = useReadChapterMutation();
  const chapterBottomRef = useDoActionWhenVisible(() => {
    mutate([chapterId]);
    handleReadChapter(chapterId);
    trackOpenWithFaireFaireAcademyReadReceiptsViewView(
      pageState,
      guide.id,
      chapterId
    );
  });

  const { shouldBlockCurrentChapter, shouldBlockContentPreview } =
    useTrackChapterViewsForContentBlock(chapterId);

  if (shouldBlockCurrentChapter) {
    return <ChapterPreview chapter={chapter} shouldBlockContentPreview />;
  }

  return (
    <Suspense fallback={<div style={{ height: "2000px" }} />}>
      <ActiveChapter />
      <Spacer height="8x" />
      <ChapterCompleteFeedback
        isChapterRead={isChapterRead}
        chapterId={chapterId}
      />
      {isChapterRead ? null : <div ref={chapterBottomRef} />}
      <WhenVisibilityIsImminent customizePlaceholder={<Spacer height={520} />}>
        <ChapterPreview
          chapter={nextChapter}
          isNextChapterPreview
          isChapterRead={isChapterRead}
          shouldBlockContentPreview={shouldBlockContentPreview}
        />
      </WhenVisibilityIsImminent>
    </Suspense>
  );
};

const useTrackChapterViewsForContentBlock = (
  chapterId: IFaireAcademyChapter.ChapterIdentifier
) => {
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const [viewedChapters, setViewedChapters] = useState<Set<string>>(
    initializeViewedChapters
  );
  const isEligibleForContentBlock = !isLoggedInRetailer;
  const numViewedChapters = viewedChapters.size;
  const isAtChapterLimit = numViewedChapters >= CHAPTER_VIEWS_LIMIT;

  useEffect(() => {
    if (isEligibleForContentBlock && !isAtChapterLimit) {
      setViewedChapters(
        (prevViewedChapters) => new Set([...prevViewedChapters, chapterId])
      );
    }
  }, [isEligibleForContentBlock, isAtChapterLimit, chapterId]);

  useEffect(() => {
    if (isEligibleForContentBlock) {
      Storage.local.setItem(
        FA_CHAPTER_VIEWS_KEY,
        JSON.stringify([...viewedChapters])
      );
    }
  }, [isEligibleForContentBlock, viewedChapters]);

  const shouldBlockContentPreview =
    isEligibleForContentBlock && isAtChapterLimit;
  const shouldBlockCurrentChapter =
    isEligibleForContentBlock &&
    isAtChapterLimit &&
    !viewedChapters.has(chapterId);

  return { shouldBlockContentPreview, shouldBlockCurrentChapter };
};

const initializeViewedChapters = (): Set<string> => {
  try {
    return new Set(
      JSON.parse(Storage.local.getItem(FA_CHAPTER_VIEWS_KEY) ?? "[]")
    );
  } catch {
    return new Set();
  }
};
